<script setup>
import Overlay from '@/assets/js/src/components/Overlay.vue'
import ManualLinks from '@/assets/js/src/modules/webmasters/_components/ManualLinks.vue'
import ClientLinks from '@/assets/js/src/modules/webmasters/_components/ClientLinks.vue'
import ServerLinks from '@/assets/js/src/modules/webmasters/_components/ServerLinks.vue'
import Wordpress from '@/assets/js/src/modules/webmasters/_components/Wordpress.vue'
import ApiKey from '@/assets/js/src/modules/webmasters/_components/ApiKey.vue'
import Privacy from '@/assets/js/src/modules/webmasters/_components/Privacy.vue'
import { getActivePinia, storeToRefs, } from 'pinia'
import { useLangStore, } from '@/assets/js/src/modules/lang/_pinia/lang'
import { useWebmastersApi, } from '@/assets/js/src/modules/webmasters/_composables/useWebmastersApi'
import LoadingDummyCustom from '@/assets/js/src/components/loading/LoadingDummyCustom'
import { useHeadInfoTitle, } from '@/assets/js/src/layout/useHeadInfo'
import { computed, } from 'vue'
import { useTranslation, } from '@/assets/js/src/util/composables'

const activePinia = getActivePinia()
const { locale, } = storeToRefs(useLangStore(activePinia))

const {
    asyncStatusLoaded,
    messagesLoaded,
    bibles,
    meta,
} = useWebmastersApi()

useHeadInfoTitle({ titleKey: 'async.webmasters.meta.title', descriptionKey: 'async.webmasters.meta.description', })
const { t, } = useTranslation()

const phpCode = computed(() => {
    return `
// ${t('async.webmasters.server_links.comment')}
$text = '${t('async.webmasters.server_links.result_raw')}';
$param = array(
    'key' => '${t('async.webmasters.server_links.api_key')}',
    'text' => $text,
    'lang' => '${locale.value}',
    'trl' => '${meta.value.bible ? meta.value.bible : ''}'
);

$ch = curl_init();
curl_setopt($ch, CURLOPT_URL, 'https://www.bibleserver.com/api/parser');
curl_setopt($ch, CURLOPT_REFERER, 'http://'.$_SERVER['SERVER_NAME']);
curl_setopt($ch, CURLOPT_POST, true);
curl_setopt($ch, CURLOPT_POSTFIELDS, $param);
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);

$result = curl_exec($ch);
$httpcode = curl_getinfo($ch, CURLINFO_HTTP_CODE);

if ($httpcode === 200) {
    $text = $result;
}

curl_close($ch);
                `
})

const javascriptCode = computed(() => {
    return '<!doctype html>\n' +
        '<html>\n' +
        '<head>\n' +
        '    <title>[...]</title>\n' +
        ' \n' +
        '    <!-- ' + t('async.webmasters.client_links.comment') + ' -->\n' +
        '    <script type="text/javascript">\n' +
        '    (function() {\n' +
        '        var ga = document.createElement(\'script\');\n' +
        '        ga.src = \'https://www.bibleserver.com/api/parser.js?key=' + t('async.webmasters.client_links.api_key') + '&lang=' + locale.value + '\';\n' +
        '        ga.setAttribute(\'async\', \'true\');\n' +
        '        document.documentElement.firstChild.appendChild(ga);\n' +
        '    })();\n' +
        ' \n' +
        '    var bsQuery = \'.article\'; // CSS-Selektor für Wrapper der Suche (optional)\n' +
        '    var bsTrl   = \'' + (meta.value.bible ? meta.value.bible : '') + '\';      // Verlinkte Übersetzung (optional)\n' +
        // eslint-disable-next-line no-useless-escape -- the unescaped closing script tag would end this .vue file's own script-setup block during SFC parsing
        '    <\/script>\n' +
        '</head>\n' +
        '<body>\n' +
        '    [...]\n' +
        '    <h1>Johannes 3</h1>\n' +
        '    <div class="article">\n' +
        '        <p>' + t('async.webmasters.client_links.result_raw') + '</p>\n' +
        '    </div>\n' +
        '    [...]\n' +
        '</body>\n' +
        '</html>'
})

const handleClick = function (event) {
    if (event.target.nodeName === 'A' && event.target.hash.trim() !== '') {
        event.preventDefault()
        scrollTo(event.target.hash)
    }
}

const scrollTo = function (target) {
    let elem = document.getElementById(target.replace('#', ''))

    if (typeof elem !== 'undefined' && elem !== null) {
        let header = document.querySelector('.bs-header-wrapper')
        let banner = document.querySelector('.banner-wrapper__banner')

        let offset = 0

        if (typeof header !== 'undefined' && header !== null) {
            offset += parseInt(header.offsetHeight)
        }

        if (typeof banner !== 'undefined' && banner !== null) {
            offset += parseInt(banner.offsetHeight)
        }

        animateScroll(elem.offsetTop - offset)
    } else if (target.indexOf('top') !== -1) {
        animateScroll(0)
    }
}

const animateScroll = function (to) {
    let pos = window.pageYOffset
    let step = 20
    let nextStep = window.pageYOffset

    let interval = setInterval(() => {

        if (pos >= to) {
            nextStep -= step

            if (nextStep <= to) {
                nextStep = to
            }
        } else {
            nextStep += step

            if (nextStep >= to) {
                nextStep = to
            }
        }

        window.scrollTo(0, nextStep)

        if (nextStep === to) {
            clearInterval(interval)
        }
    }, 10)
}
</script>

<template>
    <overlay
        class-name="webmasters-view"
        @click="handleClick($event)"
    >
        <div v-if="messagesLoaded">
            <h1 class="mb-4 hidden-print-only">
                {{ t('async.webmasters.heading') }}
            </h1>
            <div
                v-if="messagesLoaded"
                v-html="t('async.webmasters.text')"
            ></div>
            <manual-links
                v-if="messagesLoaded && asyncStatusLoaded"
                id="manual-links"
                :bibles="bibles"
                :locale="locale"
            ></manual-links>
            <server-links
                v-if="messagesLoaded"
                :code="phpCode"
                :languages="meta.languages"
            ></server-links>
            <client-links
                v-if="messagesLoaded"
                :code="javascriptCode"
                :languages="meta.languages"
            ></client-links>
            <wordpress v-if="messagesLoaded"></wordpress>
            <api-key v-if="messagesLoaded"></api-key>
            <privacy v-if="messagesLoaded"></privacy>
        </div>
        <div
            v-else
            :key="'trans-dummy-wrapper'"
        >
            <loading-dummy-custom
                key="dummy_0"
                height="56px"
                width="80%"
                margin="4px 0 24px 0"
            ></loading-dummy-custom>
            <loading-dummy-custom
                key="dummy_1"
                height="56px"
                width="80%"
                margin="15px 0 30px 0"
            ></loading-dummy-custom>
            <loading-dummy-custom
                key="dummy_2_1"
                height="16px"
                margin="0 0 3px 0"
            ></loading-dummy-custom>
            <loading-dummy-custom
                key="dummy_2_2"
                height="16px"
                margin="0 0 3px 0"
            ></loading-dummy-custom>
            <loading-dummy-custom
                key="dummy_3_1"
                height="16px"
                margin="38px 0 3px 0"
            ></loading-dummy-custom>
            <loading-dummy-custom
                key="dummy_3_2"
                height="16px"
                width="80%"
                margin="0 0 3px 0"
            ></loading-dummy-custom>
            <loading-dummy-custom
                key="dummy_3_3"
                height="16px"
                margin="0 0 3px 0"
            ></loading-dummy-custom>
            <loading-dummy-custom
                key="dummy_4_1"
                height="16px"
                margin="38px 0 3px 0"
            ></loading-dummy-custom>
            <loading-dummy-custom
                key="dummy_4_2"
                height="16px"
                margin="0 0 3px 0"
            ></loading-dummy-custom>
            <loading-dummy-custom
                key="dummy_5_1"
                height="16px"
                margin="38px 0 3px 0"
            ></loading-dummy-custom>
            <loading-dummy-custom
                key="dummy_5_2"
                height="16px"
                width="80%"
                margin="0 0 3px 0"
            ></loading-dummy-custom>
            <loading-dummy-custom
                key="dummy_5_3"
                height="16px"
                margin="0 0 3px 0"
            ></loading-dummy-custom>
            <loading-dummy-custom
                key="dummy_6_1"
                height="16px"
                margin="38px 0 3px 0"
            ></loading-dummy-custom>
            <loading-dummy-custom
                key="dummy_6_2"
                height="16px"
                margin="0 0 3px 0"
            ></loading-dummy-custom>
        </div>
    </overlay>
</template>

<style lang="scss">
.bs-app .bs-overlay.webmasters-view {
    ul {
        margin: 0 0 16px;
    }

    pre {
        position: relative;
        display: flex;
    }

    code {
        display: block;
        width: 100%;
        padding: 1.2em 1.4em;
        overflow-x: auto;
        color: map-deep-get($bs-color, white);
        font-weight: normal;
        font-size: 1rem !important;
        line-height: 1.6rem;
        white-space: pre;
        background: map-deep-get($bs-color, sidebar, background);
        box-shadow: none;

        span {
            font-size: 1rem !important;
        }

        p {
            margin: 0;
            color: map-deep-get($bs-color, white);
            font-size: 1rem;
        }

        h1 {
            color: map-deep-get($bs-color, white);
            font-weight: bold;
            font-size: 1.25rem;
        }

        &::after {
            content: "";
        }

        a {
            font-size: 1rem;
        }
    }

    code::before {
        position: absolute;
        top: 3px;
        right: 10px;
        color: map-deep-get($bs-color, greyDark);
        font-family: Roboto, sans-serif;
        content: "Text";
    }

    code.highlight::before {
        content: "";
    }

    .mb-2 {
        margin-bottom: 8px !important;
    }

    .mb-3 {
        margin-bottom: 16px !important;
    }

    .mt-4 {
        margin-top: 24px !important;
    }

    .mt-5 {
        margin-top: 48px !important;
    }
}
</style>
